<script setup lang="ts">
    import { computed, useSlots } from 'vue';
    import { __ } from '../stores/expressions';

    const props = defineProps<{
        color?: 'blueberry' | 'acai' | 'plum' | 'raspberry' | 'avocado';
        label?: string;
        size?: 'sm' | 'md' | 'lg' | 'xl';
        photoUrl?: string;
    }>();

    const slots = useSlots();

    const colorClass = computed(() => {
        switch (props.color) {
            case 'blueberry':
                return 'bg-primary-blueberry text-primary-acai';
            case 'acai':
                return 'bg-primary-acai text-primary-blueberry';
            case 'plum':
                return 'bg-primary-plum-100 text-primary-plum';
            case 'raspberry':
                return 'bg-primary-raspberry-200 text-primary-plum';
            case 'avocado':
                return 'bg-primary-avocado-100 text-primary-avocado';
            default:
                return 'bg-primary-acai text-primary-blueberry';
        }
    });

    const sizeClass = computed(() => {
        switch (props.size) {
            case 'sm':
                return 'w-7 h-7 text-caption-bold';
            case 'lg':
                return 'w-12 h-12 heading-lg-light';
            case 'xl':
                return 'w-20 h-20 heading-xl';
            case 'md':
            default:
                return 'w-9 h-9 text-body-bold';
        }
    });

    const combinedClass = computed(() => {
        return `${sizeClass.value} ${colorClass.value} ${props.photoUrl ? 'border border-gray-light' : ''}`;
    });

    const initials = computed(() => {
        const words = props.label?.trim().split(/\s+/);

        if (!words) return '';

        if (words.length === 0) return '';
        if (words.length === 1) return words[0][0].toUpperCase();

        const first = words[0][0].toUpperCase();
        const last = words[words.length - 1][0].toUpperCase();

        return first + last;
    });
</script>

<template>
    <div
        class="rounded-full overflow-hidden flex-shrink-0 aspect-square shadow-xs"
        :class="combinedClass"
    >
        <div v-if="photoUrl" class="w-full h-full">
            <img
                :src="photoUrl"
                :alt="label ? label : __('user-avatar')"
                class="w-full h-full object-cover"
            />
        </div>
        <div v-else-if="slots.default" class="w-full h-full flex items-center justify-center">
            <slot />
        </div>
        <div v-else-if="label" class="w-full h-full flex items-center justify-center">
            {{ initials }}
        </div>
    </div>
</template>
